import { useId, useState, type ReactNode } from 'react';

/**
 * How each assistant is pointed at the MCP endpoint, with the endpoint and
 * the token already in the text — so setting one up is Copy, paste, done,
 * rather than reading a manual and retyping a URL.
 *
 * Two ways in, and the tabs say which each client takes. Claude Code takes
 * a token in a header. ChatGPT, Claude.ai and Claude Desktop's connectors
 * sign in with OAuth: give them the endpoint, and the browser comes here
 * to the consent page — no token to paste at all. Desktop keeps the
 * config-file route as well, for a machine where the connector is not on
 * the plan.
 */

export type McpClient = 'claude-code' | 'claude-desktop' | 'chatgpt';

export const MCP_CLIENTS: { id: McpClient; label: string }[] = [
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'claude-desktop', label: 'Claude Desktop' },
  { id: 'chatgpt', label: 'ChatGPT' },
];

/** What stands in for the token until one is made. */
export const TOKEN_PLACEHOLDER = '<token>';

export const claudeCodeCommand = (endpoint: string, token = TOKEN_PLACEHOLDER) =>
  `claude mcp add --transport http sonasi ${endpoint} --header "Authorization: Bearer ${token}"`;

/**
 * The `mcpServers` entry for Claude Desktop, which speaks stdio only and
 * reaches an HTTP server through `mcp-remote`.
 *
 * ⚠️ The header value goes through an environment variable, and the `--header`
 * argument has no space after the colon: Claude Desktop does not escape a
 * space inside an argument, so `"Authorization: Bearer x"` reaches mcp-remote
 * as three arguments and the server sees no token. This is the form
 * mcp-remote's own README gives for Claude Desktop.
 */
export const claudeDesktopConfig = (endpoint: string, token = TOKEN_PLACEHOLDER) =>
  JSON.stringify(
    {
      mcpServers: {
        sonasi: {
          command: 'npx',
          args: ['-y', 'mcp-remote', endpoint, '--header', 'Authorization:${AUTH_HEADER}'],
          env: { AUTH_HEADER: `Bearer ${token}` },
        },
      },
    },
    null,
    2,
  );

export function McpConnectGuide({ endpoint, token }: { endpoint: string; token?: string }) {
  const [client, setClient] = useState<McpClient>('claude-code');
  // One panel, re-filled per tab, so every tab controls the same id and the
  // panel is named by whichever tab is selected.
  const base = useId();
  const panelId = `${base}-panel`;

  return (
    <div className="flex flex-col gap-2.5 border-t pt-3" style={{ borderColor: 'var(--line)' }}>
      <span className="flex flex-wrap items-center gap-2.5">
        <span className="field-label">Connect</span>
        <span
          role="tablist"
          aria-label="Assistant"
          className="inline-flex rounded-lg border p-0.5"
          style={{ background: 'var(--panel2)', borderColor: 'var(--line)' }}
        >
          {MCP_CLIENTS.map((one) => (
            <button
              key={one.id}
              id={`${base}-${one.id}`}
              type="button"
              role="tab"
              aria-selected={one.id === client}
              aria-controls={panelId}
              onClick={() => setClient(one.id)}
              className="seg focus-ring inline-flex min-h-[26px] cursor-pointer items-baseline px-2.5 py-[5px] text-[11.5px]"
            >
              {one.label}
            </button>
          ))}
        </span>
        {token && client !== 'chatgpt' ? (
          <span className="text-[11px]" style={{ color: 'var(--ok)' }}>
            Filled in with the token you just made.
          </span>
        ) : null}
      </span>

      <div
        role="tabpanel"
        id={panelId}
        aria-labelledby={`${base}-${client}`}
        className="flex flex-col gap-2"
      >
        {client === 'claude-code' ? (
          <ClaudeCode endpoint={endpoint} token={token} />
        ) : client === 'claude-desktop' ? (
          <ClaudeDesktop endpoint={endpoint} token={token} />
        ) : (
          <ChatGpt endpoint={endpoint} />
        )}
      </div>
    </div>
  );
}

function ClaudeCode({ endpoint, token }: { endpoint: string; token?: string }) {
  return (
    <Steps>
      <li>Make a token above and copy it. Give it at least the scopes you want Claude to use.</li>
      <li>
        In a terminal, run:
        <Snippet key={token} text={claudeCodeCommand(endpoint, token)} />
      </li>
      <li>
        <code className="font-mono">claude mcp list</code> should show{' '}
        <code className="font-mono">sonasi</code> as connected. Then ask Claude Code — “list my
        posts”, “write a draft post about…”. It reads the writing guide the server offers before
        writing.
      </li>
    </Steps>
  );
}

function ClaudeDesktop({ endpoint, token }: { endpoint: string; token?: string }) {
  return (
    <div className="flex flex-col gap-2.5">
      <p className="m-0 text-[12px] leading-[1.55]">
        <strong>Simplest — a connector, signed in with OAuth.</strong> No token needed.
      </p>
      <Steps>
        <li>
          In Claude Desktop (or claude.ai), open Settings › Connectors › Add custom connector.
        </li>
        <li>
          Name it <code className="font-mono">Sonasi</code> and paste the URL:
          <Snippet text={endpoint} />
        </li>
        <li>
          Add it, then choose Connect. The browser opens this admin: sign in if asked, tick what
          Claude may do, type your password, and Allow. Back in Claude, the tools are on.
        </li>
      </Steps>
      <p className="m-0 text-[12px] leading-[1.55]">
        <strong>Or — the config file, with a token.</strong> For a plan without custom connectors.
      </p>
      <Steps>
        <li>
          The config file only launches local (stdio) servers, so it reaches this one through{' '}
          <code className="font-mono">mcp-remote</code>, which needs Node.js 18 or newer on the
          machine (<code className="font-mono">node -v</code> to check).
        </li>
        <li>
          Settings › Developer › Edit Config opens{' '}
          <code className="font-mono">claude_desktop_config.json</code> — on macOS in{' '}
          <code className="font-mono">~/Library/Application Support/Claude/</code>, on Windows in{' '}
          <code className="font-mono">%APPDATA%\Claude\</code>. Put this in it, next to any servers
          already under <code className="font-mono">mcpServers</code>:
          <Snippet key={token} text={claudeDesktopConfig(endpoint, token)} />
        </li>
        <li>
          Quit Claude Desktop fully and open it again. If it reports{' '}
          <code className="font-mono">spawn npx ENOENT</code>, the app cannot see Node installed by
          nvm or fnm — put the full path from <code className="font-mono">which npx</code> in{' '}
          <code className="font-mono">command</code>. The token lives in{' '}
          <code className="font-mono">AUTH_HEADER</code>, and the header argument has no space after
          the colon, on purpose — Claude Desktop splits an argument at a space.
        </li>
      </Steps>
    </div>
  );
}

function ChatGpt({ endpoint }: { endpoint: string }) {
  return (
    <Steps>
      <li>
        In ChatGPT, turn on Developer mode (Settings › Apps &amp; Connectors › Advanced), then
        Create a connector. Needs a paid plan.
      </li>
      <li>
        Name it <code className="font-mono">Sonasi</code>, paste the MCP server URL, and choose
        OAuth as the authentication — leave client ID and secret empty; ChatGPT registers itself:
        <Snippet text={endpoint} />
      </li>
      <li>
        Create, then Connect. The browser opens this admin: sign in if asked, tick what ChatGPT may
        do, type your password, and Allow. The connection lasts 90 days, then asks again; revoke it
        any time from the list above.
      </li>
      <li>
        In a chat, enable the Sonasi connector (the + menu) and ask — “list my posts”, “write a
        draft post about…”.
      </li>
    </Steps>
  );
}

function Steps({ children }: { children: ReactNode }) {
  return (
    <ol className="m-0 flex list-decimal flex-col gap-1.5 pl-5 text-[12px] leading-[1.55]">
      {children}
    </ol>
  );
}

/**
 * One block to copy, with a button that says whether the copy took.
 *
 * Keyed on the token by its callers: "Copied" is about the text that was
 * there when the button was pressed, and Done swapping the token for the
 * placeholder must take the word with it.
 */
function Snippet({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <span className="mt-1 flex items-start gap-2">
      <pre className="field field-mono m-0 min-w-0 flex-1 overflow-x-auto whitespace-pre py-1.5 text-[11.5px]">
        {text}
      </pre>
      <button type="button" onClick={copy} className="btn-small focus-ring cursor-pointer">
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}
